import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";

import { SearchTableInput } from "../SearchTableInput/SearchTableInput";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchPromptLibrary } from "./services/fetchPromptLibrary.service";
import { updatePromptLibrary } from "./services/updatePromptLibrary.service";
import { createPromptLibrary } from "./services/createPromptLibrary.service";
import { deletePromptLibrary } from "./services/deletePromptLibrary.service";

import { INewPrompt, IPrompt } from "./interfaces/prompt.interface";
import { IOption, ITeam } from "../../interfaces/team.interface";

import { AddPromptModal } from "./components/AddPromptModal";
import { ModalConfirmWindow } from "../ManageUserTab/components/ModalConfirmWindow/ModalConfirmWindow";

import { canUserEdit } from "../../helpers/userRights";
import { defineRight } from "../ManageUserTab/helpers/defineRight";
import { useUserContext } from "../../../../context/UserContext";

import { AiOutlineEdit } from "react-icons/ai";
import { DeleteButton } from "../../../../components/Admin";
import { FaPlus, FaSpinner } from "react-icons/fa";

interface IPromptLibraryProps {
  darkMode: boolean;
  selectedTeam: ITeam | null;
  teamOptions: IOption[] | [];
  activeTab: string;
}

type ResponsiveMode = "desktop" | "compact";

const cn = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(" ");

const LOADER_ROW_H = 56;

/**
 * Collapse only on truly narrow layouts.
 * Everything above this keeps the desktop column layout,
 * and horizontal scroll is allowed if needed.
 */
const COMPACT_BREAKPOINT = 760;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const PromptRow: React.FC<any> = ({ prompt, data, index }) => {
  const {
    setShowModal,
    canEditThisTab,
    t,
    highlightText,
    activePromptTab,
    setEditModalPrompt,
    setShowAddPromptModal,
    showTeamInline,
    hasTeamColumn,
    hasActionsColumn,
    showActionsInline,
    TABLE_GRID_STYLE,
  } = data;

  const actions = (
    <>
      <button
        onClick={() => {
          setEditModalPrompt(prompt);
          setShowAddPromptModal(true);
        }}
        className={cn(
          "rounded-lg inline-flex items-center justify-center transition",
          showActionsInline ? "h-8 w-8" : "h-9 w-9",
          "hover:bg-slate-100 dark:hover:bg-slate-800",
          `tooltip-edit-prompt-${prompt.id}`,
        )}
        aria-label={t("promptLibrary.editPrompt")}
      >
        <AiOutlineEdit className="text-slate-700 dark:text-slate-100" size={16} />
      </button>
      <Tooltip
        anchorSelect={`.tooltip-edit-prompt-${prompt.id}`}
        place="top"
        className="text-xs font-light z-50"
        content={t("promptLibrary.editPrompt")}
      />

      <DeleteButton
        onClick={() => setShowModal({ visible: true, id: prompt.id })}
        className={`tooltip-delete-prompt-${prompt.id}`}
      />
      <Tooltip
        anchorSelect={`.tooltip-delete-prompt-${prompt.id}`}
        place="top"
        className="text-xs font-light z-50"
        content={t("promptLibrary.deletePrompt")}
      />
    </>
  );

  return (
    <div
      className={[
        "grid items-stretch text-sm",
        "border-b border-slate-200 dark:border-slate-700",
        "divide-x divide-slate-200 dark:divide-slate-700",
        index % 2 === 0 ? "bg-white/50 dark:bg-slate-900/15" : "bg-transparent",
        "hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors",
      ].join(" ")}
      style={{
        ...TABLE_GRID_STYLE,
        boxSizing: "border-box",
      }}
    >
      <div className="px-4 py-3 min-w-0 overflow-hidden flex items-center">
        <div className="min-w-0 w-full">
          <div
            className="min-w-0 overflow-hidden truncate font-semibold text-slate-600 dark:text-slate-300"
            dangerouslySetInnerHTML={{ __html: highlightText(prompt.name || "-") }}
          />

          {showTeamInline && activePromptTab === "all" && (
            <div
              className="mt-1 min-w-0 overflow-hidden truncate text-xs text-slate-500 dark:text-slate-400"
              dangerouslySetInnerHTML={{
                __html: highlightText(
                  prompt.team?.name || t("promptLibrary.personal", { defaultValue: "Personal" }),
                ),
              }}
            />
          )}
        </div>
      </div>

      <div className="px-4 py-3 min-w-0 overflow-hidden">
        <div
          className={cn(
            "min-w-0",
            showActionsInline ? "flex flex-col justify-center h-full" : "flex items-center h-full",
          )}
        >
          <div
            className={cn(
              "min-w-0 overflow-hidden text-slate-600 dark:text-slate-300",
              showActionsInline ? "line-clamp-3" : "line-clamp-2",
            )}
            dangerouslySetInnerHTML={{ __html: highlightText(prompt.text || "-") }}
          />

          {showActionsInline && canEditThisTab && (
            <div className="mt-2 flex items-center justify-end gap-1">{actions}</div>
          )}
        </div>
      </div>

      {hasTeamColumn && (
        <div className="px-4 py-3 min-w-0 overflow-hidden flex items-center">
          <span
            className="block min-w-0 max-w-full overflow-hidden truncate text-slate-600 dark:text-slate-300"
            dangerouslySetInnerHTML={{
              __html: highlightText(
                prompt.team?.name || t("promptLibrary.personal", { defaultValue: "My Prompts" }),
              ),
            }}
          />
        </div>
      )}

      {hasActionsColumn && canEditThisTab && (
        <div className="px-4 py-3 flex items-center justify-center gap-2">{actions}</div>
      )}
    </div>
  );
};

const PromptLibrary: React.FC<IPromptLibraryProps> = ({
  darkMode,
  selectedTeam,
  teamOptions,
  activeTab,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState<string>("");
  const [activePromptTab, setActivePromptTab] = useState<"all" | "my">("all");

  const [showAddPromptModal, setShowAddPromptModal] = useState(false);
  const [editModalPrompt, setEditModalPrompt] = useState<IPrompt | null>(null);

  const [prompts, setPrompts] = useState<IPrompt[]>([]);
  const [showModal, setShowModal] = useState<{
    visible: boolean;
    id: number | null;
  }>({ visible: false, id: null });

  const listAreaRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [listAreaWidth, setListAreaWidth] = useState<number>(0);

  useEffect(() => {
    const update = () => {
      requestAnimationFrame(() => {
        const el = listAreaRef.current;
        if (!el) return;

        const w = el.clientWidth;
        if (w > 0) setListAreaWidth(w);
      });
    };

    update();

    const RO = (window as any).ResizeObserver;
    let ro: any = null;
    if (RO && listAreaRef.current) {
      ro = new RO(() => update());
      ro.observe(listAreaRef.current);
    }

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const canEditTeamPrompts = selectedTeam ? canUserEdit(user, selectedTeam) : true;
  const canEditThisTab = activePromptTab === "my" ? true : canEditTeamPrompts;

  const allowedCreateRights = useMemo(() => new Set(["Admin", "Superuser", "Editor"]), []);
  const allowedTeamOptions = useMemo(() => {
    return (teamOptions || []).filter((opt: IOption) => {
      if (!user) return false;
      if ((user as any).is_superuser) return true;

      const teamId = Number(opt.value);
      if (!teamId) return false;

      const fakeTeam = { id: teamId } as any;
      const right = defineRight(user as any, fakeTeam as any);
      return right ? allowedCreateRights.has(right) : false;
    });
  }, [teamOptions, user, allowedCreateRights]);

  const normalizeTeam = useCallback(
    (team: any): ITeam | null => {
      if (team == null) return null;

      if (typeof team === "object" && "id" in team) {
        return team as ITeam;
      }

      const teamId = Number(team?.id ?? team?.value ?? team);
      if (!Number.isFinite(teamId) || teamId <= 0) return null;

      const option = (teamOptions || []).find((opt) => Number(opt.value) === teamId);

      let name = "";
      if (typeof team?.name === "string") {
        name = team.name;
      } else if (option?.label) {
        name = option.label;
      } else if (selectedTeam?.id === teamId) {
        name = selectedTeam.name;
      }

      return { id: teamId, name } as ITeam;
    },
    [selectedTeam, teamOptions],
  );

  const normalizePrompt = useCallback(
    (prompt: any): IPrompt => ({
      ...prompt,
      team: normalizeTeam(prompt?.team),
    }),
    [normalizeTeam],
  );

  const { data: promptList, isLoading, isFetching } = useQuery({
    queryKey: ["promptList"],
    queryFn: () => fetchPromptLibrary(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { mutate: mutateCreatePrompt } = useMutation({
    mutationFn: async (prompt: INewPrompt) => createPromptLibrary(prompt),
    onSuccess: (createdPrompt: IPrompt) => {
      const normalized = normalizePrompt(createdPrompt);

      setPrompts((prev) => [...prev, normalized]);
      queryClient.setQueryData<IPrompt[]>(["promptList"], (old) =>
        Array.isArray(old) ? [...old, normalized] : [normalized],
      );

      toast.success(t("promptLibrary.addPromptToaster"));
    },
    onError: (error: any) => {
      toast.error(String(error));
      console.error("Error creating a new Prompt:", error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["promptList"] });
    },
  });

  const { mutate: mutateUpdatePrompt } = useMutation({
    mutationFn: async (data: IPrompt) => updatePromptLibrary(data),

    onMutate: async (draftPrompt: IPrompt) => {
      await queryClient.cancelQueries({ queryKey: ["promptList"] });

      const previousPrompts = queryClient.getQueryData<IPrompt[]>(["promptList"]) ?? prompts;
      const optimisticPrompt = normalizePrompt(draftPrompt);

      const nextPrompts = previousPrompts.map((p) =>
        p.id === optimisticPrompt.id ? { ...p, ...optimisticPrompt } : p,
      );

      setPrompts(nextPrompts);
      queryClient.setQueryData(["promptList"], nextPrompts);

      return { previousPrompts };
    },

    onSuccess: (updatedPrompt: IPrompt) => {
      const normalized = normalizePrompt(updatedPrompt);

      setPrompts((prev) => prev.map((p) => (p.id === normalized.id ? normalized : p)));

      queryClient.setQueryData<IPrompt[]>(["promptList"], (old) =>
        Array.isArray(old)
          ? old.map((p) => (p.id === normalized.id ? normalized : p))
          : [normalized],
      );

      toast.success(t("promptLibrary.promptChangeToaster"));
    },

    onError: (error: any, _draft, context) => {
      if (context?.previousPrompts) {
        setPrompts(context.previousPrompts);
        queryClient.setQueryData(["promptList"], context.previousPrompts);
      }

      toast.error(String(error));
      console.error("Error updating the Prompt:", error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["promptList"] });
    },
  });

  const { mutate: mutateDeletedPrompt } = useMutation({
    mutationFn: async (id: number) => deletePromptLibrary(id),
    onSuccess: (_data, deletedId) => {
      setPrompts((prev) => prev.filter((p) => p.id !== deletedId));
      queryClient.setQueryData<IPrompt[]>(["promptList"], (old) =>
        Array.isArray(old) ? old.filter((p) => p.id !== deletedId) : [],
      );

      toast.success(t("promptLibrary.deletePromptToaster"));
      setShowModal({ visible: false, id: null });
    },
    onError: (error: any) => {
      toast.error(String(error));
      setShowModal({ visible: false, id: null });
      console.error("Error deleting the Prompt:", error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["promptList"] });
    },
  });

  useEffect(() => {
    if (Array.isArray(promptList)) {
      setPrompts(promptList.map(normalizePrompt));
    }
  }, [promptList, normalizePrompt]);

  useEffect(() => {
    setActivePromptTab("all");
  }, [selectedTeam]);

  useEffect(() => {
    setEditModalPrompt(null);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeTab, selectedTeam]);

  const getSearchValue = (inputValue: string) => setSearchValue(inputValue.toLowerCase());

  const highlightText = useCallback(
    (text: string) => {
      if (!searchValue) return text;
      const safeSearch = escapeRegExp(searchValue);
      const regex = new RegExp(`(${safeSearch})`, "gi");
      return text.replace(
        regex,
        (match) =>
          `<span class='bg-yellow-300 text-slate-600 dark:text-slate-300 font-semibold'>${match}</span>`,
      );
    },
    [searchValue],
  );

  const filteredPrompts = useMemo(() => {
    return prompts
      .filter((prompt: any) => {
        if (activePromptTab === "my") return prompt.team === null;

        if (activePromptTab === "all") {
          if (!selectedTeam) return true;
          return !!prompt.team && prompt.team.id === selectedTeam.id;
        }

        return true;
      })
      .filter((prompt: any) => {
        if (!searchValue) return true;
        const lowerText = (prompt.text || "").toLowerCase();
        const lowerTitle = (prompt.name || "").toLowerCase();
        const lowerTeam = (prompt.team?.name || "").toLowerCase();

        return (
          lowerText.includes(searchValue) ||
          lowerTitle.includes(searchValue) ||
          lowerTeam.includes(searchValue)
        );
      });
  }, [prompts, activePromptTab, selectedTeam, searchValue]);

  const responsiveMode = useMemo<ResponsiveMode>(() => {
    if (listAreaWidth > 0 && listAreaWidth < COMPACT_BREAKPOINT) return "compact";
    return "desktop";
  }, [listAreaWidth]);

  const hasTeamColumn = activePromptTab === "all" && responsiveMode === "desktop";
  const showTeamInline = activePromptTab === "all" && responsiveMode === "compact";

  const hasActionsColumn = canEditThisTab && responsiveMode === "desktop";
  const showActionsInline = canEditThisTab && responsiveMode === "compact";

  const tableMinWidth = useMemo(() => {
    if (responsiveMode === "compact") {
      return 200 + 280;
    }

    let w = 240 + 320;
    if (hasTeamColumn) w += 220;
    if (hasActionsColumn) w += 140;
    return w;
  }, [responsiveMode, hasTeamColumn, hasActionsColumn]);

  const GRID_STYLE = useMemo<React.CSSProperties>(() => {
    let cols: string[] = [];

    if (responsiveMode === "compact") {
      cols = ["minmax(200px,260px)", "minmax(280px,1fr)"];
    } else {
      cols = ["minmax(240px,320px)", "minmax(320px,1fr)"];
      if (hasTeamColumn) cols.push("minmax(220px,220px)");
      if (hasActionsColumn) cols.push("minmax(140px,140px)");
    }

    return { gridTemplateColumns: cols.join(" ") };
  }, [responsiveMode, hasTeamColumn, hasActionsColumn]);

  const TABLE_GRID_STYLE = useMemo<React.CSSProperties>(
    () => ({
      ...GRID_STYLE,
      width: "100%",
      minWidth: `${tableMinWidth}px`,
    }),
    [GRID_STYLE, tableMinWidth],
  );

  const handleAddPrompt = (newPrompt: INewPrompt) => mutateCreatePrompt(newPrompt);

  const handleUpdatePrompt = (prompt: IPrompt) => {
    mutateUpdatePrompt(prompt);
    setEditModalPrompt(null);
    setShowAddPromptModal(false);
  };

  const handleDeletePrompt = () => {
    if (showModal.id == null) return;
    mutateDeletedPrompt(showModal.id);
  };

  const showLoaderRow = filteredPrompts.length > 0 && isFetching && !isLoading;

  const promptTabs = [
    {
      key: selectedTeam
        ? `${selectedTeam.name} ${t("promptLibrary.promptsTitle")}`
        : t("promptLibrary.allPrompts"),
      label: "all" as const,
    },
    { key: t("promptLibrary.prompts"), label: "my" as const },
  ];

  const showingText = useMemo(() => {
    const shown = filteredPrompts.length;
    const total = prompts.length;
    const fallback = `Showing ${shown} of ${total} prompts`;

    const translated = t("promptLibrary.showing", {
      shown,
      total,
      count: shown,
      filtered: shown,
      filteredCount: shown,
      totalCount: total,
      prompts: total,
      totalPrompts: total,
      defaultValue: fallback,
    });

    if (
      typeof translated === "string" &&
      (translated.includes("...") || translated.includes("{{") || translated.includes("undefined"))
    ) {
      return fallback;
    }

    return translated;
  }, [t, filteredPrompts.length, prompts.length]);

  const btnBase =
    "inline-flex items-center justify-center h-10 px-4 rounded-lg text-sm font-medium transition";
  const btnGreen = cn(
    btnBase,
    "bg-del-green dark:bg-del-dark-theme-dark-green text-white hover:opacity-90",
  );

  const HeaderRow = useCallback(
    () => (
      <div
        className={[
          "grid items-center h-11 sticky top-0 z-20",
          "bg-white dark:bg-slate-900",
          "text-xs font-semibold uppercase tracking-wide",
          "text-slate-600 dark:text-slate-300",
          "border-b border-slate-200 dark:border-slate-700",
          "divide-x divide-slate-200 dark:divide-slate-700",
        ].join(" ")}
        style={{
          ...TABLE_GRID_STYLE,
          boxSizing: "border-box",
        }}
      >
        <div className="px-4 py-3 min-w-0 overflow-hidden truncate whitespace-nowrap">
          {t("promptLibrary.title")}
        </div>

        <div className="px-4 py-3 min-w-0 overflow-hidden truncate whitespace-nowrap">
          {t("promptLibrary.text")}
        </div>

        {hasTeamColumn && (
          <div className="px-4 py-3 min-w-0 overflow-hidden truncate whitespace-nowrap">
            {t("promptLibrary.team")}
          </div>
        )}

        {hasActionsColumn && (
          <div className="px-4 py-3 text-center min-w-0 overflow-hidden truncate whitespace-nowrap">
            {t("adminPanel.columns.actions", { defaultValue: "Actions" })}
          </div>
        )}
      </div>
    ),
    [t, TABLE_GRID_STYLE, hasTeamColumn, hasActionsColumn],
  );

  const itemData = useMemo(
    () => ({
      setShowModal,
      canEditThisTab,
      t,
      highlightText,
      activePromptTab,
      setEditModalPrompt,
      setShowAddPromptModal,
      showTeamInline,
      hasTeamColumn,
      hasActionsColumn,
      showActionsInline,
      TABLE_GRID_STYLE,
    }),
    [
      canEditThisTab,
      t,
      highlightText,
      activePromptTab,
      showTeamInline,
      hasTeamColumn,
      hasActionsColumn,
      showActionsInline,
      TABLE_GRID_STYLE,
    ],
  );

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t("promptLibrary.titlePage", { defaultValue: "Prompt Library" })}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("promptLibrary.subtitle", {
                defaultValue: "Browse and manage your prompt templates",
              })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => setShowAddPromptModal(true)} className={btnGreen}>
              <FaPlus className="mr-2" />
              {t("promptLibrary.addPrompts")}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-1">
            {promptTabs.map((tab) => {
              const active = activePromptTab === tab.label;

              return (
                <button
                  key={tab.label}
                  onClick={() => {
                    setActivePromptTab(tab.label);
                    setEditModalPrompt(null);
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollTop = 0;
                    }
                  }}
                  className={cn(
                    "px-3 h-9 rounded-md text-sm font-medium transition",
                    active
                      ? "bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-slate-100"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100",
                  )}
                >
                  {tab.key}
                </button>
              );
            })}
          </div>

          <div className="flex-1 min-w-[260px] max-w-md">
            <SearchTableInput
              onInputChange={getSearchValue}
              placeholderText={t("promptLibrary.searchPrompt")}
            />
          </div>
        </div>

        {showAddPromptModal && (
          <AddPromptModal
            setShowAddPromptModal={setShowAddPromptModal}
            selectedTeam={
              activePromptTab === "all" && selectedTeam
                ? (user as any)?.is_superuser ||
                  allowedTeamOptions.some((o) => Number(o.value) === selectedTeam.id)
                  ? selectedTeam
                  : null
                : null
            }
            handleAddPrompt={handleAddPrompt}
            teamListOptions={allowedTeamOptions}
            darkMode={darkMode}
            initialPrompt={editModalPrompt}
            onSubmit={handleUpdatePrompt}
          />
        )}
      </div>

      <div className="flex-1 min-h-0 bg-slate-100 dark:bg-slate-950/20 p-6 overflow-hidden">
        <div className="h-full min-h-0 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="text-center p-6">
              <FaSpinner
                className="animate-spin inline-block"
                size={24}
                color={darkMode ? "#FFFFFF" : "#000000"}
              />
            </div>
          ) : (
            <>
              <div ref={listAreaRef} className="flex-1 min-h-0 relative flex flex-col">
                {filteredPrompts.length === 0 ? (
                  <div ref={scrollContainerRef} className="h-full min-h-0 overflow-auto">
                    <div style={{ minWidth: tableMinWidth }}>
                      <HeaderRow />
                      <div className="p-6 flex items-center justify-center min-h-[220px]">
                        <p className="text-sm text-slate-600 dark:text-slate-300">{t("noData")}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div ref={scrollContainerRef} className="h-full min-h-0 overflow-auto">
                    <div style={{ minWidth: tableMinWidth }}>
                      <HeaderRow />

                      {filteredPrompts.map((prompt, index) => (
                        <PromptRow
                          key={prompt.id ?? index}
                          prompt={prompt}
                          index={index}
                          data={itemData}
                        />
                      ))}

                      {showLoaderRow && (
                        <div
                          className="flex items-center justify-center"
                          style={{ height: LOADER_ROW_H }}
                        >
                          <FaSpinner
                            className="animate-spin"
                            size={18}
                            color={darkMode ? "#FFFFFF" : "#000000"}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-6 py-3 text-sm text-slate-600 dark:text-slate-300">
                {showingText}
              </div>
            </>
          )}
        </div>

        {showModal.visible && (
          <ModalConfirmWindow
            setShowModal={setShowModal as any}
            handleDeleteUser={handleDeletePrompt}
            message={t("promptLibrary.deletePromptMsg")}
          />
        )}
      </div>
    </div>
  );
};

export { PromptLibrary };